import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  deleteUser,
  updateProfile,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  type User,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';

/** Primary auth provider we can interactively reauthenticate against. */
export type PrimaryProviderId = 'password' | 'google.com';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthReady: boolean;
  error: string | null;

  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  reauthenticate: (args?: { password?: string }) => Promise<void>;
  deleteAccount: () => Promise<void>;
  getPrimaryProviderId: () => PrimaryProviderId | null;
  clearError: () => void;

  _setUser: (user: User | null) => void;
  _setAuthReady: () => void;
}

const googleProvider = new GoogleAuthProvider();

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      isLoading: false,
      isAuthReady: false,
      error: null,

      signInWithEmail: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          await signInWithEmailAndPassword(auth, email, password);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      signUpWithEmail: async (email, password, displayName) => {
        set({ isLoading: true, error: null });
        try {
          const { user } = await createUserWithEmailAndPassword(auth, email, password);
          await updateProfile(user, { displayName });
          // Force refresh the user object so displayName is available
          set({ user: auth.currentUser, isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign up failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      signInWithGoogle: async () => {
        set({ isLoading: true, error: null });
        try {
          await signInWithPopup(auth, googleProvider);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Google sign in failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      signOut: async () => {
        set({ isLoading: true, error: null });
        try {
          await firebaseSignOut(auth);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign out failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      resetPassword: async (email) => {
        set({ isLoading: true, error: null });
        try {
          await sendPasswordResetEmail(auth, email);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Password reset failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /**
       * Interactively reauthenticate the current user.
       *
       * For email/password users, the caller must pass `{ password }`. For Google users,
       * this triggers a popup and ignores any password argument. On success, the user has
       * a fresh auth token, which unblocks destructive operations like deleteUser().
       */
      reauthenticate: async (args) => {
        const user = get().user;
        if (!user) throw new Error('No user signed in');

        const providerId = user.providerData[0]?.providerId;

        set({ isLoading: true, error: null });
        try {
          if (providerId === 'google.com') {
            await reauthenticateWithPopup(user, googleProvider);
          } else if (providerId === 'password') {
            if (!user.email) throw new Error('User has no email on record');
            const password = args?.password;
            if (!password) throw new Error('Password is required');
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);
          } else {
            throw new Error(`Unsupported provider: ${providerId ?? 'unknown'}`);
          }
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Reauthentication failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      deleteAccount: async () => {
        const user = get().user;
        if (!user) return;

        set({ isLoading: true, error: null });
        try {
          await deleteUser(user);
          set({ user: null, isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Account deletion failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      /** Returns the primary provider id for the current user, or null if unsupported/none. */
      getPrimaryProviderId: () => {
        const user = get().user;
        if (!user) return null;
        const id = user.providerData[0]?.providerId;
        if (id === 'password' || id === 'google.com') return id;
        return null;
      },

      clearError: () => set({ error: null }),

      _setUser: (user) => set({ user }),
      _setAuthReady: () => set({ isAuthReady: true }),
    }),
    { name: 'auth-store' }
  )
);
